import { CreatorSignupForm } from "@/components/auth/creator-signup-form";
import { LogoMarquee } from "@/components/marketing/logo-marquee";
import { sessionQueryOptions } from "@/lib/queries/session";
import "@/styles/join.css";
import {
  createFileRoute,
  isRedirect,
  Link,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";

const LOGIN_URL = "https://creators.aspireiq.com/login";

const searchSchema = z.object({
  // Present when returning from a social-OAuth round-trip. We only treat the
  // explicit truthy values true/"true"/"1" as true — z.coerce.boolean would
  // coerce ANY non-empty string (e.g. "false") to true and skip the guard.
  sso_callback: z
    .preprocess((v) => v === true || v === "true" || v === "1", z.boolean())
    .optional(),
});

export const Route = createFileRoute("/(auth)/join")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, search }) => {
    // On the OAuth return trip the user IS authenticated — let the component
    // mount so it can finish creating the creator org + persisting details.
    if (search.sso_callback === true) return;

    // Otherwise behave like /login: send already-authenticated users home.
    try {
      const session = await context.queryClient.fetchQuery(
        sessionQueryOptions(),
      );
      if (session?.user && session?.session) {
        throw redirect({ to: "/" });
      }
    } catch (error) {
      if (isRedirect(error)) throw error;
    }
  },
  component: JoinPage,
});

const AREAS = [
  {
    title: "Marketplace",
    desc: "Browse campaigns from hundreds of brands and apply in seconds.",
    icon: "store",
  },
  {
    title: "Campaigns",
    desc: "Track progress, complete tasks, and manage deliverables in one place.",
    icon: "clipboard",
  },
  {
    title: "Profiles",
    desc: "Build your profile, connect channels, and set up payment details.",
    icon: "user",
  },
  {
    title: "Dashboard",
    desc: "See your campaigns, collaborations, and tasks the moment you log in.",
    icon: "dashboard",
  },
  {
    title: "Team",
    desc: "Invite collaborators and talent agents, and assign roles.",
    icon: "users",
  },
  {
    title: "Support",
    desc: "Manage account preferences, themes, and support resources.",
    icon: "support",
  },
] as const;

function AreaIcon({ icon }: { icon: (typeof AREAS)[number]["icon"] }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (icon) {
    case "store":
      return (
        <svg {...common}>
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <path d="M3 6h18" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      );
    case "clipboard":
      return (
        <svg {...common}>
          <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <path d="M12 11h4" />
          <path d="M12 16h4" />
          <path d="M8 11h.01" />
          <path d="M8 16h.01" />
        </svg>
      );
    case "user":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="5" />
          <path d="M20 21a8 8 0 0 0-16 0" />
        </svg>
      );
    case "dashboard":
      return (
        <svg {...common}>
          <rect width="7" height="9" x="3" y="3" rx="1" />
          <rect width="7" height="5" x="14" y="3" rx="1" />
          <rect width="7" height="9" x="14" y="12" rx="1" />
          <rect width="7" height="5" x="3" y="16" rx="1" />
        </svg>
      );
    case "users":
      return (
        <svg {...common}>
          <path d="M18 21a8 8 0 0 0-16 0" />
          <circle cx="10" cy="8" r="5" />
          <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
        </svg>
      );
    case "support":
      return (
        <svg {...common}>
          <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
        </svg>
      );
  }
}

function JoinPage() {
  const search = Route.useSearch();
  return (
    <div className="join-page">
      <header className="bare">
        <div className="wrap">
          <Link to="/join" className="logo" aria-label="Aspire">
            <img
              src="/app-assets/aspire/logos/aspire-logo.svg"
              alt="Aspire"
              width={108}
              height={27}
              fetchPriority="high"
            />
          </Link>
        </div>
      </header>

      <section className="hero">
        <div className="wrap">
          <h1>
            Your dream brand
            <br />
            is on <em>Aspire</em>
          </h1>
          <p className="sub">
            Connect with the best brands on Aspire. We don't take a percentage
            of your deals, and it's completely free to sign up.
          </p>
          <p className="free-note">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            No fees. No percentage. Keep 100% of what you earn.
          </p>
        </div>
      </section>

      <LogoMarquee />

      <CreatorSignupForm loginUrl={LOGIN_URL} resumeSso={search.sso_callback} />

      <section className="caps">
        <div className="wrap">
          <h2>What you can do on Aspire</h2>
          <div className="cap-flow">
            <div className="cap-track">
              <span className="fill" />
            </div>
            <div className="cap-node n1">
              <div className="cap-ico">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </div>
              <div className="cap-label">
                Discover brands<small>Browse active campaigns</small>
              </div>
            </div>
            <div className="cap-node n2">
              <div className="cap-ico">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m22 2-7 20-4-9-9-4Z" />
                  <path d="M22 2 11 13" />
                </svg>
              </div>
              <div className="cap-label">
                Apply in seconds<small>Pitch the brands you love</small>
              </div>
            </div>
            <div className="cap-node n3">
              <div className="cap-ico">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="cap-label">
                Collaborate<small>Create &amp; deliver content</small>
              </div>
            </div>
            <div className="cap-node n4">
              <div className="cap-ico">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <div className="cap-label">
                Get paid<small>Keep 100% of your earnings</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="areas">
        <div className="wrap">
          <div className="lead">
            <h2>One portal for every part of the job</h2>
            <p>
              From discovering your next campaign to managing the work and
              getting paid, Aspire brings it all together.
            </p>
          </div>
          <div className="area-grid">
            {AREAS.map((area) => (
              <div className="area" key={area.title}>
                <div className="ic">
                  <AreaIcon icon={area.icon} />
                </div>
                <h3>{area.title}</h3>
                <p>{area.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="close-band">
        <div className="wrap close-inner">
          <h2>Start working with today's best brands on Aspire</h2>
          <p>
            Sign up free, get on the radar of top brands, and keep every dollar
            you earn.
          </p>
          <a href="#signup" className="btn btn-cream">
            Sign up now
          </a>
          <div className="login-link">
            Already on Aspire? <a href={LOGIN_URL}>Log in</a>
          </div>
        </div>
      </section>
    </div>
  );
}
